import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  useKameleoonVisitedPageEvent,
  usePurchase,
  useRateSummary,
  useResponsive,
} from '../../../hooks';

import PurchasePage from './PurchasePage';
import { QuestionsInfo } from '../QuestionsInfo';
import { BILLING_CYCLE_TYPE, PetType } from '../../../constants';
import { FigoLoadingOverlay } from '../../common/FigoLoadingOverlay';

import dogIcon from '../../../assets/dog-selected.svg';
import catIcon from '../../../assets/cat-selected.svg';
import backIcon from '../../../assets/purchase/back.svg';

import { currencyFormat, useRetryPurchase } from '../../../util';
import { URL_PURCHASE, URL_PURCHASE_SUCCESS_ERROR } from '../../../routes';

import './PurchaseRetryScreen.css';
import { StripeForm } from '../../common/stripe/StripeForm';

const PurchaseButtons = ({
  loading,
  onReturnSummary,
  t,
  doPurchase,
}) => (
  <>
    <button
      className="Purchase-retry-button"
      data-kameleoon-destination={URL_PURCHASE}
      disabled={loading}
      onClick={doPurchase}
      type="button"
    >
      {t('retryPurchase.retryButton')}
    </button>

    <button
      className="Purchase-retry-button-return"
      disabled={loading}
      onClick={onReturnSummary}
      type="button"
    >
      <img alt="" aria-hidden src={backIcon} />
      {t('retryPurchase.returnButton')}
    </button>
  </>
);

export const PurchaseRetryScreen = () => {
  const { t } = useTranslation('purchaseResult');
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting);
  const { isMobile } = useResponsive();
  const stripeRef = useRef();
  useKameleoonVisitedPageEvent();

  const { getSelectedRate } = useRateSummary();
  const { doPurchase } = usePurchase();

  const {
    isMonthlyPlan,
    paymentMethod,
    purchase,
    rateSummary: { loading, PetQuotes, QuoteSummaries },
  } = store;

  const { paymentMethodType } = paymentMethod;

  const onReturnSummary = () => {
    history.goBack();
  };

  const totalToday = useMemo(() => {
    if (loading) {
      return '$0.00';
    }

    const billingCycle = isMonthlyPlan
      ? BILLING_CYCLE_TYPE.monthly : BILLING_CYCLE_TYPE.annualy;
    const paymentType = paymentMethodType?.value;
    const summary = QuoteSummaries
      ?.find((item) => item.BillingCycle === billingCycle
        && item.PaymentType === paymentType)
      || { TodaysTotal: 0 };

    return `$${currencyFormat({ value: summary.TodaysTotal })}`;
  }, [QuoteSummaries, isMonthlyPlan, loading, paymentMethodType?.value]);

  useLayoutEffect(() => {
    getSelectedRate();
  }, [getSelectedRate]);

  useEffect(() => {
    if (purchase.success) {
      const goToPurchaseScreen = async () => {
        const url = purchase.incompletePurchases?.length > 0
          ? URL_PURCHASE_SUCCESS_ERROR : URL_PURCHASE;

        history.push(url);
      };

      goToPurchaseScreen();
    }
  }, [dispatch, history, purchase.success, purchase.incompletePurchases]);

  const handlePurchase = useCallback(() => {
    doPurchase({ isRetryPurchase: true });
  }, [doPurchase]);

  if (!useRetryPurchase) {
    return null;
  }

  return (
    <PurchasePage>
      <FigoLoadingOverlay visible={loading || purchase.loading} />

      <div className="Purchase-retry-cards-container">
        <div className="Purchase-retry-container Purchase-retry-card">
          <h1>{t('retryPurchase.title')}</h1>

          <p className="Purchase-retry-summary-p">
            {t('retryPurchase.summary')}
          </p>

          <StripeForm
            ref={stripeRef}
            formClass="Purchase-stripe-form"
            noButton
            noEdit
            onConfirm={handlePurchase}
          />

          {!isMobile && (
            <PurchaseButtons
              doPurchase={handlePurchase}
              loading={loading || purchase.loading}
              onReturnSummary={onReturnSummary}
              t={t}
            />
          )}
        </div>

        <div className="Purchase-retry-container-right Purchase-retry-card">
          <h3>{t('retryPurchase.purchaseSummary')}</h3>

          <div className="Purchase-retry-container-mobile">
            <ul>
              {!loading && PetQuotes.map((pet) => (
                <li key={String(pet.PetQuoteId)}>
                  <img
                    alt=""
                    src={pet.PetType === PetType.Cat.value ? catIcon : dogIcon}
                  />

                  <div className="Column">
                    <span className="Purchase-retry-pet-name">
                      {pet.PetName}
                    </span>

                    <span className="Purchase-retry-pet-price">
                      {t(
                        'retryPurchase.petPrice',
                        { monthlyPrice: pet.MonthlyPremium.toFixed(2) },
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="Purchase-retry-today-total-div">
              <span>
                {t('retryPurchase.totalToday')}
              </span>

              <span className="Purchase-retry-today-total">
                {totalToday}
              </span>
            </div>

            {isMobile && (
              <div className="Column">
                <PurchaseButtons
                  doPurchase={handlePurchase}
                  loading={loading || purchase.loading}
                  onReturnSummary={onReturnSummary}
                  t={t}
                />
              </div>
            )}
          </div>

          <div className="Purchase-retry-questions">
            <QuestionsInfo />
          </div>
        </div>
      </div>
    </PurchasePage>
  );
};
